import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Editor, Frame, Element, useEditor } from "@craftjs/core";
import { toast } from "react-toastify";
import {
  Container,
  Text,
  Button,
  Image,
  HeroBanner,
  ProductShowcase,
  FeaturesList,
  PromoBanner,
  TestimonialsSection,
  ContactSection,
  FooterSection,
  Divider,
} from "./custom-builder/CraftComponents";
import { CraftToolbox } from "./custom-builder/CraftToolbox";
import { CraftSettingsPanel } from "./custom-builder/CraftSettingsPanel";
import { RenderNode } from "./custom-builder/RenderNode";
import { CRAFT_DEFAULT_STATE } from "./custom-builder/CraftDefaultTemplate";
import { storeSettingsService } from "../../services/storeSettings.service";
import { getBusiness } from "../../services/auth.service";
import { getProducts } from "../../services/inventory.service";
import { CraftStoreContext } from "./custom-builder/CraftStoreContext";
import { getStorefrontPath } from "./storefrontPaths";
import {
  MdSave,
  MdCheckCircle,
  MdArrowBack,
  MdDesktopWindows,
  MdTabletAndroid,
  MdPhoneIphone,
  MdUndo,
  MdRedo,
  MdVisibility,
  MdVisibilityOff,
  MdRefresh,
  MdAutoAwesome,
  MdOpenInNew,
  MdClose,
  MdViewModule,
  MdTune,
} from "react-icons/md";
import { LogoLoading } from "../../components/common/utlity/utility";

// Registered resolver components for Craft.js
const CRAFT_RESOLVER = {
  Container,
  Text,
  Button,
  Image,
  HeroBanner,
  ProductShowcase,
  FeaturesList,
  PromoBanner,
  TestimonialsSection,
  ContactSection,
  FooterSection,
  Divider,
};

// Internal Header Bar with Actions (inside Craft.js Editor context)
const BuilderTopBar = ({
  themeName,
  setThemeName,
  deviceViewport,
  setDeviceViewport,
  isPreviewMode,
  setIsPreviewMode,
  onSave,
  onApply,
  isSaving,
  isApplying,
  isActiveCustomTheme,
  onResetTemplate,
  storefrontUrl,
  onBack,
}) => {
  const { actions, canUndo, canRedo } = useEditor((state, query) => ({
    canUndo: query.history.canUndo(),
    canRedo: query.history.canRedo(),
  }));

  return (
    <header className='min-h-14 sm:h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-2 sm:px-4 py-1.5 flex items-center justify-between gap-2 z-30 shadow-sm shrink-0'>
      {/* Left: Back & Theme Name */}
      <div className='flex items-center gap-1.5 sm:gap-3 min-w-0'>
        <button
          type='button'
          onClick={onBack}
          className='p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors'
          title='Back to Themes'
        >
          <MdArrowBack size={20} />
        </button>

        <div className='h-6 w-[1px] bg-gray-200 dark:bg-gray-800 hidden sm:block' />

        <div className='flex items-center gap-2 min-w-0'>
          <div className='w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-600 to-purple-600 text-white items-center justify-center shadow-sm shrink-0 hidden sm:flex'>
            <MdAutoAwesome size={18} />
          </div>
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                value={themeName}
                onChange={(e) => setThemeName(e.target.value)}
                className='text-sm font-bold text-gray-900 dark:text-white bg-transparent border-b border-transparent hover:border-gray-300 focus:border-indigo-500 outline-none transition-colors px-1 w-28 sm:w-44 min-w-0'
                placeholder='Custom Theme Name'
              />
              {isActiveCustomTheme && (
                <span className='bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-bold px-2 py-0.5 rounded-full hidden sm:flex items-center gap-1'>
                  <MdCheckCircle size={12} /> Active Live
                </span>
              )}
            </div>
            <p className='text-[10px] text-gray-400 dark:text-gray-500 px-1 hidden sm:block'>
              Visual Drag & Drop Page Builder
            </p>
          </div>
        </div>
      </div>

      {/* Middle: Viewport Switcher & History */}
      <div className='hidden md:flex items-center gap-2'>
        {/* Device viewports */}
        <div className='flex items-center p-1 bg-gray-100 dark:bg-gray-800 rounded-xl'>
          <button
            type='button'
            onClick={() => setDeviceViewport("desktop")}
            className={`p-1.5 rounded-lg transition-all ${
              deviceViewport === "desktop"
                ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-900"
            }`}
            title='Desktop View (100%)'
          >
            <MdDesktopWindows size={18} />
          </button>
          <button
            type='button'
            onClick={() => setDeviceViewport("tablet")}
            className={`p-1.5 rounded-lg transition-all ${
              deviceViewport === "tablet"
                ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-900"
            }`}
            title='Tablet View (768px)'
          >
            <MdTabletAndroid size={18} />
          </button>
          <button
            type='button'
            onClick={() => setDeviceViewport("mobile")}
            className={`p-1.5 rounded-lg transition-all ${
              deviceViewport === "mobile"
                ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-900"
            }`}
            title='Mobile View (375px)'
          >
            <MdPhoneIphone size={18} />
          </button>
        </div>

        <div className='h-5 w-[1px] bg-gray-200 dark:bg-gray-800 mx-1' />

        {/* Undo / Redo */}
        <div className='flex items-center gap-1'>
          <button
            type='button'
            disabled={!canUndo}
            onClick={() => actions.history.undo()}
            className={`p-2 rounded-lg transition-colors ${
              canUndo
                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
            }`}
            title='Undo (Ctrl+Z)'
          >
            <MdUndo size={18} />
          </button>
          <button
            type='button'
            disabled={!canRedo}
            onClick={() => actions.history.redo()}
            className={`p-2 rounded-lg transition-colors ${
              canRedo
                ? "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
            }`}
            title='Redo (Ctrl+Y)'
          >
            <MdRedo size={18} />
          </button>
        </div>

        {/* Reset Template */}
        <button
          type='button'
          onClick={onResetTemplate}
          className='p-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1'
          title='Reset to default starter template'
        >
          <MdRefresh size={16} />
          <span>Reset</span>
        </button>
      </div>

      {/* Right: Preview, Save & Apply Buttons */}
      <div className='flex items-center gap-1.5 sm:gap-2.5 shrink-0'>
        {/* Mobile undo / redo */}
        <div className='flex md:hidden items-center'>
          <button
            type='button'
            disabled={!canUndo}
            onClick={() => actions.history.undo()}
            className='p-1.5 rounded-lg text-gray-700 dark:text-gray-300 disabled:text-gray-300 dark:disabled:text-gray-600'
            title='Undo'
          >
            <MdUndo size={18} />
          </button>
          <button
            type='button'
            disabled={!canRedo}
            onClick={() => actions.history.redo()}
            className='p-1.5 rounded-lg text-gray-700 dark:text-gray-300 disabled:text-gray-300 dark:disabled:text-gray-600'
            title='Redo'
          >
            <MdRedo size={18} />
          </button>
        </div>
        {/* Toggle Live Preview Mode */}
        <button
          type='button'
          onClick={() => setIsPreviewMode(!isPreviewMode)}
          className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl text-xs font-bold transition-all ${
            isPreviewMode
              ? "bg-amber-500 text-white shadow-md hover:bg-amber-600"
              : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
          }`}
          title='Toggle between editing and interactive preview'
        >
          {isPreviewMode ? (
            <MdVisibilityOff size={16} />
          ) : (
            <MdVisibility size={16} />
          )}
          <span className='hidden sm:inline'>
            {isPreviewMode ? "Exit Preview" : "Preview"}
          </span>
        </button>

        {/* Storefront Link */}
        {storefrontUrl && (
          <button
            type='button'
            onClick={() =>
              window.open(storefrontUrl, "_blank", "noopener,noreferrer")
            }
            className='p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl transition-colors hidden lg:flex'
            title='View Live Storefront'
          >
            <MdOpenInNew size={18} />
          </button>
        )}

        {/* Save Button */}
        <button
          type='button'
          onClick={onSave}
          disabled={isSaving || isApplying}
          className='flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-white dark:bg-gray-800 border-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl text-xs font-bold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <MdSave size={16} />
          <span className='hidden sm:inline'>{isSaving ? "Saving..." : "Save Layout"}</span>
          <span className='sm:hidden'>{isSaving ? "..." : "Save"}</span>
        </button>

        {/* Apply Button */}
        <button
          type='button'
          onClick={onApply}
          disabled={isSaving || isApplying}
          className='flex items-center gap-1.5 px-2.5 sm:px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl text-xs font-bold shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <MdCheckCircle size={16} />
          <span className='hidden sm:inline'>{isApplying ? "Applying..." : "Apply Theme"}</span>
          <span className='sm:hidden'>{isApplying ? "..." : "Apply"}</span>
        </button>
      </div>
    </header>
  );
};

// Main Visual Builder Canvas Controller
const BuilderCanvasWrapper = ({
  deviceViewport,
  setDeviceViewport,
  initialData,
  setInitialData,
  themeName,
  setThemeName,
  isPreviewMode,
  setIsPreviewMode,
  isActiveCustomTheme,
  setIsActiveCustomTheme,
  bussinessUuid,
  businessName,
  storefrontUrl,
  onBack,
}) => {
  const { actions, query } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [mobilePanel, setMobilePanel] = useState(null);
  const hasDeserializedRef = useRef(false);

  // Load initial serialized data into canvas if available (ONLY once on initial load)
  useEffect(() => {
    if (initialData && !hasDeserializedRef.current) {
      try {
        hasDeserializedRef.current = true;
        const dataStr =
          typeof initialData === "string"
            ? initialData
            : JSON.stringify(initialData);

        if (dataStr && dataStr.trim() && dataStr !== "{}") {
          actions.deserialize(dataStr);
        }
      } catch (err) {
        console.warn(
          "Failed to deserialize saved layout, using default template",
          err,
        );
      }
    }
  }, [initialData, actions]);

  // Sync editor enabled mode with preview toggle
  useEffect(() => {
    actions.setOptions((options) => {
      options.enabled = !isPreviewMode;
    });
  }, [isPreviewMode, actions]);

  // Handle Save Layout
  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Commit any active inline text edits before serializing
      if (
        document.activeElement &&
        typeof document.activeElement.blur === "function"
      ) {
        document.activeElement.blur();
      }
      await new Promise((resolve) => setTimeout(resolve, 60));

      const serializedJson = query.serialize();
      const payload = {
        themeId: "custom",
        name: themeName || "Custom Theme",
        serializedData: serializedJson,
        craftData: JSON.parse(serializedJson),
      };

      const response = await storeSettingsService.saveCustomThemeLayout(
        bussinessUuid,
        payload,
      );
      if (response && response.success) {
        if (typeof setInitialData === "function") {
          setInitialData(serializedJson);
        }
        toast.success(
          response.message || "Custom theme layout saved successfully!",
        );
      } else {
        toast.error(response?.error || "Failed to save theme layout");
      }
    } catch (err) {
      console.error("Save custom layout error:", err);
      toast.error(
        err.response?.data?.error ||
          err.message ||
          "Error saving custom theme layout",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Apply / Activate Theme
  const handleApply = async () => {
    try {
      setIsApplying(true);

      // Commit any active inline text edits before serializing
      if (
        document.activeElement &&
        typeof document.activeElement.blur === "function"
      ) {
        document.activeElement.blur();
      }
      await new Promise((resolve) => setTimeout(resolve, 60));

      // 1. First save the latest serialized tree
      const serializedJson = query.serialize();
      const payload = {
        themeId: "custom",
        name: themeName || "Custom Theme",
        serializedData: serializedJson,
        craftData: JSON.parse(serializedJson),
      };

      await storeSettingsService.saveCustomThemeLayout(bussinessUuid, payload);
      if (typeof setInitialData === "function") {
        setInitialData(serializedJson);
      }

      // 2. Activate the custom theme
      const activateRes = await storeSettingsService.activateCustomThemeLayout(
        bussinessUuid,
        "custom",
      );
      if (activateRes && activateRes.success) {
        setIsActiveCustomTheme(true);
        toast.success("Custom Theme applied & published to your live store!");

        // 3. Open live storefront after a short delay
        if (storefrontUrl) {
          setTimeout(() => {
            window.open(storefrontUrl, "_blank", "noopener,noreferrer");
          }, 600);
        }
      } else {
        toast.error(activateRes?.error || "Failed to activate custom theme");
      }
    } catch (err) {
      console.error("Apply custom layout error:", err);
      toast.error(
        err.response?.data?.error ||
          err.message ||
          "Error applying custom theme layout",
      );
    } finally {
      setIsApplying(false);
    }
  };


  // Handle Reset to Default Template
  const handleResetTemplate = () => {
    if (
      window.confirm(
        "Are you sure you want to reset the canvas to the starter template?",
      )
    ) {
      actions.deserialize(JSON.stringify(CRAFT_DEFAULT_STATE));
      toast.info("Reset to starter store template");
    }
  };

  // Viewport width styling
  const viewportWidths = {
    desktop: "w-full max-w-full",
    tablet: "w-[768px] max-w-full",
    mobile: "w-[375px] max-w-full",
  };

  return (
    <div className='flex flex-col h-screen overflow-hidden bg-gray-100 dark:bg-gray-950 font-sans'>
      {/* Top Action Bar */}
      <BuilderTopBar
        themeName={themeName}
        setThemeName={setThemeName}
        deviceViewport={deviceViewport}
        setDeviceViewport={setDeviceViewport}
        isPreviewMode={isPreviewMode}
        setIsPreviewMode={setIsPreviewMode}
        onSave={handleSave}
        onApply={handleApply}
        isSaving={isSaving}
        isApplying={isApplying}
        isActiveCustomTheme={isActiveCustomTheme}
        onResetTemplate={handleResetTemplate}
        storefrontUrl={storefrontUrl}
        onBack={onBack}
      />

      {/* Workspace Area: Left Toolbox | Center Canvas | Right Settings */}
      <div className='flex-1 flex overflow-hidden relative'>
        {/* Mobile backdrop */}
        {!isPreviewMode && mobilePanel && (
          <div
            className='fixed inset-0 bg-black/40 z-30 lg:hidden'
            onClick={() => setMobilePanel(null)}
          />
        )}

        {/* Left Component Toolbox */}
        {!isPreviewMode && (
          <div
            className={`w-72 max-w-[85vw] shrink-0 h-full overflow-hidden shadow-lg z-40 bg-white dark:bg-gray-900 fixed top-0 bottom-0 left-0 transition-transform duration-300 lg:static lg:translate-x-0 ${
              mobilePanel === "toolbox" ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <button
              type='button'
              onClick={() => setMobilePanel(null)}
              className='lg:hidden absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              title='Close'
            >
              <MdClose size={18} />
            </button>
            <CraftToolbox />
          </div>
        )}

        {/* Canvas Center Area */}
        <div className='flex-1 min-w-0 h-full overflow-y-auto p-2 sm:p-6 pb-16 lg:pb-6 flex justify-center items-start bg-slate-200/70 dark:bg-gray-950/80'>
          <div
            className={`transition-all duration-300 min-h-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-300/80 dark:border-gray-800 ${viewportWidths[deviceViewport]}`}
          >
            {/* Viewport Frame Badge */}
            <div className='bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between text-[11px] text-gray-500 select-none'>
              <span className='font-semibold capitalize flex items-center gap-1.5'>
                <span className='w-2 h-2 rounded-full bg-green-500 inline-block' />
                Live Page Canvas ({deviceViewport})
              </span>
              <span className='text-[10px] text-gray-400'>
                {isPreviewMode
                  ? "Preview Mode • Interactivity Enabled"
                  : "Edit Mode • Drag & Drop Blocks"}
              </span>
            </div>

            {/* Craft.js Frame */}
            <div
              className='p-4 sm:p-6 min-h-[calc(100vh-140px)]'
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes("application/craft-block")) {
                  e.preventDefault(); // Allow drop
                  e.dataTransfer.dropEffect = "copy";
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                const blockId = e.dataTransfer.getData("application/craft-block");
                if (blockId) {
                  // Find if they dropped exactly onto a specific node
                  const el = document.elementFromPoint(e.clientX, e.clientY);
                  const nodeWrapper = el?.closest('[data-craft-node-id]');
                  const targetNodeId = nodeWrapper ? nodeWrapper.getAttribute('data-craft-node-id') : null;

                  window.dispatchEvent(
                    new CustomEvent("craft-html5-drop", {
                      detail: { blockId, targetNodeId },
                    })
                  );
                }
              }}
            >
              <Frame>
                <Element
                  is={Container}
                  canvas
                  background='#ffffff'
                  padding={16}
                  borderRadius={0}
                  minHeight='100vh'
                >
                  <HeroBanner />
                  <FeaturesList />
                  <ProductShowcase />
                  <PromoBanner />
                  <TestimonialsSection />
                  <ContactSection />
                  <FooterSection />
                </Element>
              </Frame>
            </div>
          </div>
        </div>

        {/* Right Settings Inspector */}
        {!isPreviewMode && (
          <div
            className={`w-80 max-w-[90vw] shrink-0 h-full overflow-hidden shadow-lg z-40 bg-white dark:bg-gray-900 fixed top-0 bottom-0 right-0 transition-transform duration-300 lg:static lg:translate-x-0 ${
              mobilePanel === "settings" ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <button
              type='button'
              onClick={() => setMobilePanel(null)}
              className='lg:hidden absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
              title='Close'
            >
              <MdClose size={18} />
            </button>
            <CraftSettingsPanel />
          </div>
        )}

        {/* Mobile bottom bar to open panels */}
        {!isPreviewMode && (
          <div className='lg:hidden fixed bottom-0 inset-x-0 z-20 flex bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]'>
            <button
              type='button'
              onClick={() => setMobilePanel("toolbox")}
              className='flex-1 flex items-center justify-center gap-2 py-3 text-xs font-bold text-gray-700 dark:text-gray-200'
            >
              <MdViewModule size={18} /> Add Blocks
            </button>
            <button
              type='button'
              onClick={() => setMobilePanel("settings")}
              className='flex-1 flex items-center justify-center gap-2 py-3 text-xs font-bold text-indigo-600 dark:text-indigo-400 border-l border-gray-200 dark:border-gray-800'
            >
              <MdTune size={18} /> Settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Root CustomThemeBuilderPage Component
export default function CustomThemeBuilderPage() {
  const { bussinessUuid, businessName } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [themeName, setThemeName] = useState("Custom Store Theme");
  const [initialData, setInitialData] = useState(null);
  const [isActiveCustomTheme, setIsActiveCustomTheme] = useState(false);
  const [storeProducts, setStoreProducts] = useState([]);
  const [deviceViewport, setDeviceViewport] = useState("desktop");
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [resolvedBusinessName, setResolvedBusinessName] = useState(
    businessName || "",
  );

  const storefrontUrl = `${getStorefrontPath({
    businessName: resolvedBusinessName,
    bussinessUuid,
  })}/`;

  const handleBackToGallery = () => {
    const targetPath = resolvedBusinessName
      ? `/${resolvedBusinessName}/${bussinessUuid}/online-store/themes`
      : `/${bussinessUuid}/online-store/themes`;
    navigate(targetPath);
  };

  // Load existing custom theme layout from database
  const loadCustomLayout = useCallback(async () => {
    try {
      setLoading(true);

      // Load business details
      const bizRes = await getBusiness(bussinessUuid);
      if (bizRes?.data?.businessName) {
        setResolvedBusinessName(bizRes.data.businessName);
      }

      // Load real store products for builder preview
      try {
        const prodRes = await getProducts(bussinessUuid);
        let pData = [];
        if (prodRes?.data?.data) {
          pData = prodRes.data.data;
        } else if (Array.isArray(prodRes?.data)) {
          pData = prodRes.data;
        } else if (Array.isArray(prodRes)) {
          pData = prodRes;
        }
        const onlineProducts = pData.filter(
          (p) => p.enableForOnlineStore !== false,
        );
        setStoreProducts(onlineProducts);
      } catch (prodErr) {
        console.warn("Could not load products for builder preview:", prodErr);
      }

      // Load active store settings
      const settingsRes =
        await storeSettingsService.getStoreSettings(bussinessUuid);
      if (
        settingsRes?.success &&
        settingsRes.data?.theme?.themeId === "custom"
      ) {
        setIsActiveCustomTheme(true);
      }

      // Load custom layout document
      const layoutRes = await storeSettingsService.getCustomThemeLayout(
        bussinessUuid,
        "custom",
      );
      if (layoutRes && layoutRes.success && layoutRes.data) {
        const doc = layoutRes.data;
        if (doc.name) setThemeName(doc.name);
        if (doc.isActive) setIsActiveCustomTheme(true);

        if (doc.serializedData) {
          setInitialData(doc.serializedData);
        } else if (doc.craftData) {
          setInitialData(doc.craftData);
        }
      }
    } catch (err) {
      console.warn(
        "No existing custom layout found, using default starter",
        err,
      );
    } finally {
      setLoading(false);
    }
  }, [bussinessUuid]);

  useEffect(() => {
    loadCustomLayout();
  }, [loadCustomLayout]);

  if (loading) {
    return <LogoLoading />;
  }

  return (
    <CraftStoreContext.Provider
      value={{
        products: storeProducts,
        onAddToCart: (prod) =>
          toast.info(
            `🛒 "${prod.productName || prod.name}" added to cart (Preview Mode)`,
          ),
        bussinessUuid,
        displayBusinessName: resolvedBusinessName,
        previewMode: isPreviewMode,
        isLiveStorefront: false,
      }}
    >
      <Editor
        resolver={CRAFT_RESOLVER}
        enabled={true}
        onRender={RenderNode}
        indicator={{
          success: "#4f46e5",
          error: "#ef4444",
          transition: "all 0.15s ease",
        }}
      >
        <BuilderCanvasWrapper
          deviceViewport={deviceViewport}
          setDeviceViewport={setDeviceViewport}
          initialData={initialData}
          setInitialData={setInitialData}
          themeName={themeName}
          setThemeName={setThemeName}
          isPreviewMode={isPreviewMode}
          setIsPreviewMode={setIsPreviewMode}
          isActiveCustomTheme={isActiveCustomTheme}
          setIsActiveCustomTheme={setIsActiveCustomTheme}
          bussinessUuid={bussinessUuid}
          businessName={resolvedBusinessName}
          storefrontUrl={storefrontUrl}
          onBack={handleBackToGallery}
        />
      </Editor>
    </CraftStoreContext.Provider>
  );

}
